import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthFetch } from '../lib/useAuthFetch';
import { Card, SectionLabel, Pill, StatTile, DataTable, DecisionSummary, type Column } from '../components/ui';

const API = import.meta.env.VITE_API_URL ?? '';

// ── Types (mirror GET /api/v1/prices/change/:eventId) ───────────────────────────
interface EventCore {
  id: string;
  alert_type: 'price_drop' | 'price_increase';
  created_at: string;
  confidence: string | null;
  competitor_id: string;
  competitor_name: string | null;
  competitor_address: string | null;
  location_id: string;
  location_name: string | null;
  product_id: string | null;
  product_name: string | null;
  product_brand: string | null;
  product_category: string | null;
  product_package_size: string | null;
  old_price: number | null;
  new_price: number | null;
  change_abs: number | null;
  change_pct: number | null;
  slot_type: 'track' | 'block' | null;
  blocked_at: string | null;
}

interface HistoryPoint {
  date: string; // YYYY-MM-DD
  price: number;
  on_promo: boolean;
  promo_text: string | null;
}

interface DeepDiveData {
  event: EventCore;
  history: HistoryPoint[];
  days: number;
}

// ── Formatting helpers ──────────────────────────────────────────────────────────
const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${n.toFixed(2)}`;
// Typographic minus for negative money so numbers align in mono.
const signedMoney = (n: number) => `${n < 0 ? '−' : '+'}$${Math.abs(n).toFixed(2)}`;
const signedPct = (n: number) => `${n < 0 ? '−' : '+'}${Math.abs(n).toFixed(1)}%`;
const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
const fmtDay = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const normalizedConfidence = (value: string | null): 'high' | 'medium' | 'low' | 'unavailable' => {
  const normalized = value?.toLowerCase();
  return normalized === 'high' || normalized === 'medium' || normalized === 'low' ? normalized : 'unavailable';
};

// ── Single-series price trend chart (real observation history) ──────────────────
const CW = 680, CH = 200, PAD = { t: 16, r: 20, b: 28, l: 46 };

function TrendChart({ history, baseline }: { history: HistoryPoint[]; baseline: number | null }) {
  if (history.length < 2) {
    return (
      <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 11, border: '1px dashed var(--border)', borderRadius: 'var(--r-sm)' }}>
        Not enough observations yet to chart a trend — the next pipeline run will add points.
      </div>
    );
  }

  const prices = history.map(h => h.price);
  const withBase = baseline != null ? [...prices, baseline] : prices;
  const rawMin = Math.min(...withBase);
  const rawMax = Math.max(...withBase);
  const pad = (rawMax - rawMin) * 0.12 || 1;
  const minP = rawMin - pad;
  const maxP = rawMax + pad;

  const n = history.length;
  const x = (i: number) => PAD.l + (i / (n - 1)) * (CW - PAD.l - PAD.r);
  const y = (p: number) => PAD.t + (1 - (p - minP) / (maxP - minP)) * (CH - PAD.t - PAD.b);

  const line = history.map((h, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(h.price).toFixed(1)}`).join(' ');
  const area = `${line} L${x(n - 1).toFixed(1)} ${y(minP).toFixed(1)} L${x(0).toFixed(1)} ${y(minP).toFixed(1)} Z`;

  // Grid: 4 evenly-spaced price lines.
  const grid = Array.from({ length: 5 }, (_, i) => minP + (maxP - minP) * (i / 4));
  // X labels: first, middle, last.
  const xIdx = [0, Math.floor((n - 1) / 2), n - 1];

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg viewBox={`0 0 ${CW} ${CH}`} style={{ width: '100%', height: 220 }} preserveAspectRatio="none">
        {grid.map((p, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={y(p)} x2={CW - PAD.r} y2={y(p)} stroke="currentColor" strokeOpacity="0.06" strokeWidth="1" />
            <text x={PAD.l - 6} y={y(p) + 3} textAnchor="end" fontFamily="JetBrains Mono,monospace" fontSize="9" fill="currentColor" fillOpacity="0.4">${p.toFixed(0)}</text>
          </g>
        ))}
        {/* Baseline = price at the moment of the event (old price) */}
        {baseline != null && (
          <line x1={PAD.l} y1={y(baseline)} x2={CW - PAD.r} y2={y(baseline)} stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 3" strokeOpacity="0.55" />
        )}
        <path d={area} fill="var(--accent)" fillOpacity="0.06" />
        <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" />
        {history.map((h, i) => (
          <circle key={i} cx={x(i)} cy={y(h.price)} r={h.on_promo ? 3.5 : 2.5} fill={h.on_promo ? 'var(--warm)' : 'var(--accent)'} stroke="var(--surface)" strokeWidth="1.5" />
        ))}
        {xIdx.map(i => (
          <text key={i} x={x(i)} y={CH - PAD.b + 16} textAnchor="middle" fontFamily="JetBrains Mono,monospace" fontSize="9" fill="currentColor" fillOpacity="0.4">{fmtDay(history[i].date)}</text>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 16, marginTop: 6, paddingLeft: PAD.l }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--text-3)' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warm)' }} /> On promo
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--text-3)' }}>
          <span style={{ width: 14, height: 0, borderTop: '1px dashed var(--accent)' }} /> Price at event ({money(baseline)})
        </span>
      </div>
    </div>
  );
}

// ── Observation-log row (daily) ─────────────────────────────────────────────────
interface ObsRow extends HistoryPoint {
  key: string;
  deltaFromPrev: number | null;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PriceChangeDeepDive() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const { eventId } = useParams<{ eventId: string }>();

  const [data, setData] = useState<DeepDiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(false);
    authFetch(`${API}/api/v1/prices/change/${eventId}`)
      .then(async r => {
        if (cancelled) return;
        if (r.status === 404) { setNotFound(true); setLoading(false); return; }
        if (!r.ok) { setError(true); setLoading(false); return; }
        const json = await r.json();
        if (cancelled) return;
        if (json?.success && json.data?.event) setData(json.data as DeepDiveData);
        else setError(true);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [eventId]);

  // ── Chrome ────────────────────────────────────────────────────────────────────
  const backBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', cursor: 'pointer', background: 'none', border: 'none', padding: 0, letterSpacing: '0.02em' };
  const sep: React.CSSProperties = { width: 1, height: 16, background: 'var(--border-2)' };

  function Topbar() {
    return (
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0' }}>
          <button style={backBtn} onClick={() => navigate(-1)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div style={sep} />
          <button style={backBtn} onClick={() => navigate('/prices')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
            Price Intelligence
          </button>
          <div style={sep} />
          <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 400, letterSpacing: '-0.015em', lineHeight: 1, color: 'var(--text-1)' }}>Price Change Deep Dive</div>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn" onClick={() => navigate('/prices/history')} style={{ fontSize: 11, padding: '7px 13px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
              Price history
            </button>
          </div>
        </div>
      </div>
    );
  }

  function Centered({ children }: { children: React.ReactNode }) {
    return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 12, padding: 24, textAlign: 'center' }}>{children}</div>;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Topbar />
        <Centered>Pulling this price change event…</Centered>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Topbar />
        <Centered>
          <div style={{ maxWidth: 380, lineHeight: 1.6 }}>
            {error
              ? "Couldn't load this price change event. We'll retry — check back shortly."
              : "This price change event isn't available. It may have been reviewed away, or you no longer hold an active track on this rival at this location."}
            <div style={{ marginTop: 14 }}>
              <button className="btn" onClick={() => navigate('/alerts')} style={{ fontSize: 11, padding: '7px 13px' }}>Back to Alert Feed</button>
            </div>
          </div>
        </Centered>
      </div>
    );
  }

  // ── Derived (real data only) ───────────────────────────────────────────────────
  const ev = data.event;
  const isDrop = ev.alert_type === 'price_drop';
  const productLabel = ev.product_name ?? 'Unknown product';
  const changeTone = isDrop ? 'accent' : 'warm';
  const latest = data.history.length ? data.history[data.history.length - 1] : null;
  const detailBits = [ev.product_category, ev.product_package_size].filter(Boolean).join(' · ');

  const obsRows: ObsRow[] = data.history.map((h, i) => ({
    ...h,
    key: h.date,
    deltaFromPrev: i === 0 ? null : Number((h.price - data.history[i - 1].price).toFixed(2)),
  }));

  const obsColumns: Column<ObsRow>[] = [
    { key: 'date', header: 'Date', mono: true, width: 96, render: r => fmtDay(r.date) },
    { key: 'price', header: 'Price', align: 'right', width: 86, render: r => `$${r.price.toFixed(2)}` },
    {
      key: 'delta', header: 'vs Prev Day', align: 'right', width: 110,
      render: r => r.deltaFromPrev == null || r.deltaFromPrev === 0
        ? <span style={{ color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>—</span>
        : <span style={{ fontFamily: 'var(--mono)', fontWeight: 600, color: r.deltaFromPrev < 0 ? 'var(--accent)' : 'var(--warm)' }}>{signedMoney(r.deltaFromPrev)}</span>,
    },
    {
      key: 'status', header: 'Status', align: 'right', width: 130,
      render: r => r.on_promo
        ? <Pill variant="chip" style={{ background: 'var(--warm-soft)', color: 'var(--warm)', textTransform: 'uppercase' }}>{r.promo_text || 'promo'}</Pill>
        : <span style={{ color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>regular</span>,
    },
  ];

  const detailRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontSize: 12, color: 'var(--text-1)', textAlign: 'right' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Topbar />

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        <DecisionSummary
          className={`price-change-summary price-change-summary--${isDrop ? 'drop' : 'increase'}`}
          question="What does this price event establish?"
          priority="watch"
          answer={ev.change_pct != null && ev.new_price != null
            ? `${ev.competitor_name ?? 'This rival'} ${isDrop ? 'reduced' : 'increased'} ${productLabel} by ${Math.abs(ev.change_pct).toFixed(1)}% to ${money(ev.new_price)}`
            : `${ev.competitor_name ?? 'This rival'} changed the listed price for ${productLabel}`}
          change={`The alert was created ${fmtDateTime(ev.created_at)}.${latest
            ? ` Supporting history contains ${data.history.length} observation${data.history.length === 1 ? '' : 's'}; the latest is ${money(latest.price)} on ${fmtDay(latest.date)}${latest.on_promo ? ' on promotion' : ''}.`
            : ' No supporting price observations are available.'}`}
          impact="Your current price, sales, and margin data are not available on this event, so market position and business exposure cannot be determined from it."
          provenance={{
            updatedAt: fmtDateTime(ev.created_at),
            source: 'price-change alert and rival observation history',
            confidence: normalizedConfidence(ev.confidence),
          }}
          action={(
            <button
              className="btn btn-primary"
              data-testid="price-change-compare-current"
              onClick={() => navigate(`/prices?location_id=${encodeURIComponent(ev.location_id)}`)}
            >
              Compare current prices →
            </button>
          )}
        >
          <div style={{ display: 'flex', gap: 7, marginTop: 12, flexWrap: 'wrap' }}>
            <Pill variant="chip" style={isDrop ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : { background: 'var(--warm-soft)', color: 'var(--warm)' }}>
              {isDrop ? 'Price Drop' : 'Price Increase'}
            </Pill>
            {ev.slot_type === 'track' && <Pill tone="tracking" variant="chip">Tracking</Pill>}
            {latest?.on_promo && (
              <Pill variant="chip" style={{ background: 'var(--warm-soft)', color: 'var(--warm)' }}>
                On promo{latest.promo_text ? ` ${latest.promo_text}` : ''}
              </Pill>
            )}
            {ev.location_name && <Pill variant="chip">{ev.location_name}</Pill>}
            {detailBits && <Pill variant="chip">{detailBits}</Pill>}
          </div>
        </DecisionSummary>

        {/* TWO-COL */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 18, alignItems: 'start' }}>
          {/* MAIN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            <Card style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-1)' }}>Price Trend — {productLabel}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                    {ev.competitor_name ?? 'Rival'} · last {data.days} days · {data.history.length} observation{data.history.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <TrendChart history={data.history} baseline={ev.old_price} />
            </Card>

            <div>
              <SectionLabel style={{ marginBottom: 10 }}>Observation History</SectionLabel>
              <Card variant="sm" style={{ padding: 0, overflow: 'hidden' }}>
                <DataTable
                  columns={obsColumns}
                  rows={obsRows}
                  rowKey={r => r.key}
                  empty="No observations recorded for this product at this rival yet — the next pipeline run will populate them."
                />
              </Card>
            </div>
          </div>

          {/* SIDE */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Card style={{ padding: '16px 18px' }}>
              <SectionLabel style={{ marginBottom: 12 }}>The Change</SectionLabel>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <StatTile size="sm" label="Was" value={money(ev.old_price)} style={{ flex: 1 }} />
                <StatTile size="sm" label="Now" tone={changeTone} value={money(ev.new_price)} style={{ flex: 1 }} />
              </div>
              <StatTile
                size="sm"
                label="Change"
                tone={changeTone}
                value={ev.change_abs != null ? signedMoney(ev.change_abs) : '—'}
                delta={ev.change_pct != null ? signedPct(ev.change_pct) : undefined}
                deltaTone={changeTone}
                style={{ width: '100%' }}
              />
            </Card>

            <Card style={{ padding: '16px 18px' }}>
              <SectionLabel style={{ marginBottom: 4 }}>Event Details</SectionLabel>
              {detailRow('Detected', fmtDateTime(ev.created_at))}
              {detailRow('Rival', ev.competitor_name ?? '—')}
              {ev.location_name && detailRow('Location', ev.location_name)}
              {detailRow('Relationship', ev.slot_type === 'track' ? <Pill tone="tracking" variant="chip">Tracking</Pill> : '—')}
              {ev.product_category && detailRow('Category', ev.product_category)}
              {detailRow('Latest observed', latest ? `$${latest.price.toFixed(2)}` : '—')}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 0' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Confidence</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-1)', textTransform: 'capitalize' }}>{ev.confidence ?? '—'}</span>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
